import * as React from 'react';

import {BillingSummary} from 'shells/billing/billing-summary/billing-summary';
import {EmptyStateLarge} from 'shells/empty-states/empty-state-large';
import {ErrorState} from 'shells/error-state/error-state';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {CopyableLink} from 'shells/link/copyable-link';
import {Link} from 'shells/link/link';
import {Body} from 'shells/typography/body';

import type {GetInvoicePreview_invoicePreview as InvoicePreview} from 'nutshell-graphql-types';

import {analyticsClient} from '../../../../../analytics-client';
import {useGetSessionUser} from '../../../../core/use-get-session-user';

import {BillingInfo} from '../../billing-info';

import {shouldShowShareButton} from '../helpers';

import {CouponField} from './coupon-field';
import {NextInvoiceDetails} from './next-invoice-details';

import styles from '../manage.module.css';

type Props = {
    invoicePreview: InvoicePreview | null | undefined;
    invoicePreviewError: string | null | undefined;
    isLoading: boolean;
    couponCode: string | null | undefined;
    onChangeCouponCode: (value?: string | null | undefined) => void;
    billingInfo: any | null | undefined;
    billingInfoError: string | null | undefined;
    isLoadingBillingInfo: boolean;
    refetchBillingInfo: () => Promise<any>;
    isTrial: boolean;
    errorUpdatingPlan: string | null | undefined;
    isViewingBillingForm: boolean;
    setIsViewingBillingForm: (arg1: boolean) => void;
    setHasUnsavedBillingChanges: (arg1: boolean) => void;
};

export function Review(props: Props) {
    const {user} = useGetSessionUser();

    const isTrial = props.isTrial;

    React.useEffect(() => {
        if (isTrial) {
            analyticsClient.track(
                analyticsClient.EVENTS.TRACK_CUSTOMER_TRIAL_VISITS_BILLING_REVIEW
            );
        }
    }, [isTrial]);

    // @ts-expect-error
    if (!window.recurly) {
        return (
            <EmptyStateLarge
                icon={
                    <img
                        alt=''
                        style={{width: 240}}
                        src='/include/images/empty-states/Error_doodle.svg'
                    />
                }
                title='Uh oh!'
                subtitle={
                    <div>
                        We weren’t able to load your billing page. If you have an ad blocker, please
                        try disabling it and reloading this page. If that doesn’t work, consider
                        trying a different browser. If you’re still having trouble,{' '}
                        <Link
                            variant='primary'
                            newTab={true}
                            href='https://support.nutshell.com/'
                            size='big'
                        >
                            contact our friendly support team for help
                        </Link>
                        .
                    </div>
                }
            />
        );
    }

    return (
        <>
            {props.errorUpdatingPlan ? (
                <div className='full-width'>
                    <ErrorState errorMessage={props.errorUpdatingPlan} />
                </div>
            ) : (
                <div className='pad-16'>
                    <StackLegacy spacing={24}>
                        <div className='flex justify-sb'>
                            <div className={styles.title}>
                                <Body size='large' isBold={true} color='dark'>
                                    Review and purchase
                                </Body>
                            </div>
                            {shouldShowShareButton(user) && (
                                <div className={styles['copyable-link-container']}>
                                    <CopyableLink
                                        name='copyLink'
                                        value={`${window.location.origin}/buy-now`}
                                        size='big'
                                        isWhite={true}
                                    />
                                </div>
                            )}
                        </div>
                        <div className={styles.container}>
                            <StackLegacy spacing={16}>
                                <StackLegacy spacing={16}>
                                    <BillingSummary
                                        plan={props.invoicePreview && props.invoicePreview.plan}
                                        isLoading={props.isLoading}
                                        errorMessage={props.invoicePreviewError}
                                        isPurchasing={true}
                                    />
                                    {!props.isLoading && !props.invoicePreviewError && (
                                        <NextInvoiceDetails invoicePreview={props.invoicePreview} />
                                    )}
                                </StackLegacy>
                                {props.isTrial && !props.invoicePreviewError && (
                                    <CouponField
                                        couponCode={props.couponCode}
                                        onChangeCouponCode={props.onChangeCouponCode}
                                        userSuppliedCouponApplied={Boolean(
                                            props.invoicePreview &&
                                                props.invoicePreview.userSuppliedCouponApplied
                                        )}
                                        errorMessage={
                                            !props.isLoading && props.invoicePreview
                                                ? props.invoicePreview.couponError
                                                : undefined
                                        }
                                        isApplyingCoupon={props.isLoading}
                                    />
                                )}
                            </StackLegacy>
                            <BillingInfo
                                billingInfo={props.billingInfo}
                                refetchBillingInfo={props.refetchBillingInfo}
                                isLoading={props.isLoadingBillingInfo}
                                errorMessage={props.billingInfoError}
                                isViewingBillingForm={props.isViewingBillingForm}
                                setIsViewingBillingForm={props.setIsViewingBillingForm}
                                setHasUnsavedBillingChanges={props.setHasUnsavedBillingChanges}
                            />
                        </div>
                    </StackLegacy>
                </div>
            )}
        </>
    );
}
